import { ref, computed, watch, reactive, nextTick } from 'vue';
import { useStore } from 'vuex';
import { nanoid } from 'nanoid';
import { Data, DateOption, Series } from '@/features/reports/models';
import { ChartType } from '@/features/reports/models/chartType';

export function useBaseReport(reportType: string, loadReportData: () => Promise<Data>) {
  const store = useStore();

  // Reactive state
  const reportId = ref(`${reportType}-${nanoid(7)}`);
  const data = reactive<Data>({ scale: { title: '', labels: [] }, series: [] });
  const chartType = ref<ChartType>(ChartType.LINE);
  const isDataLoaded = ref(false);
  const loadingError = ref(false);

  // Computed properties
  const currentWorkspace = computed(() => store.getters.currentWorkspace);
  const selectedDateOption = computed<DateOption>(() => store.getters.selectedDateOption);

  const isDataEmpty = computed(() => !data.series || data.series.length === 0);

  const showLineChart = computed(() => chartType.value === ChartType.LINE);
  const showBarChart = computed(() => chartType.value === ChartType.BAR);
  const showPercentageBarChart = computed(() => chartType.value === ChartType.PERCENTAGE_BAR);

  // Watchers
  watch(
    selectedDateOption,
    async (newFilter: DateOption, previousFilter: DateOption) => {
      if (newFilter && newFilter !== previousFilter) {
        await renderReportData();
      }
    },
    { immediate: true },
  );

  // Methods
  async function renderReportData() {
    await nextTick();
    if (!isReportCriterionValid()) return;

    isDataLoaded.value = false;
    loadingError.value = false;

    try {
      data.series = []; // Reset data before loading new values
      Object.assign(data, await loadReportData());
      isDataLoaded.value = true;
    } catch (err: any) {
      console.error('Failed to load report data', err);
      loadingError.value = true;
    }
  }

  function isReportCriterionValid() {
    return !!selectedDateOption.value;
  }

  function switchLineChart() {
    chartType.value = ChartType.LINE;
  }

  function switchBarChart() {
    chartType.value = ChartType.BAR;
  }

  function switchStackedChart() {
    chartType.value = ChartType.PERCENTAGE_BAR;
  }

  function getSeriesData(seriesName: string): Series {
    return data.series.filter((series: Series) => series.name === seriesName)[0];
  }

  return {
    reportId,
    data,
    chartType,
    isDataLoaded,
    loadingError,
    isDataEmpty,
    showLineChart,
    showBarChart,
    showPercentageBarChart,
    renderReportData,
    isReportCriterionValid,
    switchLineChart,
    switchBarChart,
    switchStackedChart,
    getSeriesData,
    currentWorkspace,
    selectedDateOption,
  };
}
